import type {EventsPrefillMorphData, EventTypeAiSearchParams, EventWizardEmbeddedEditorOptions} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {
  COVER_PREVIEW_DATA_URL_PREFIXES,
  COVER_PREVIEW_MESSAGE_PREFIX,
  EMBEDDED_EDITOR_IFRAME_ID,
  MAX_COVER_PREVIEW_DATA_URL_LENGTH,
  MediaTypes,
} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {capitalizeFirstLetterOfAllWords} from '@Utils/string.util';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {ImageData} from '@Libraries/add-media-library';
import {repoImageScreenURL} from '@Libraries/graphic-item-image-library';
import {onEventWizardCoverPhotoInstantPreviewUrlUpdate, onEventWizardCoverPhotoUpdate, onEventWizardIsCoverPhotoPreviewLoading} from '@Components/event-wizard/event-wizard-slice';
import {togglePreviewCoverPhotoLoading, updatePreviewCoverPhoto} from '@Components/event-wizard/preview/preview.helper';
import {EmbeddedEditorAction, openDesignInEmbeddedEditor, preloadEmbeddedEditorPlugin} from '@Libraries/embedded-editor';
import type {PosterPageData} from '@Libraries/poster-library';
import {getPosterThumbnailImagePreviewURL, pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import {MorphType, PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {noop} from '@Utils/general.util';
import {isPAYGUser} from '@Utils/user.util';
import {isUserLoggedIn} from '@Libraries/user.library';
import {GALLERY_DESIGN_CLICKED_EVENT} from '@Components/design-gallery/design-gallery.types';
import {getGalleryUriFromEventAiSearchParams} from '@Libraries/events.library';
import {openErrorModal} from '@Modals/error-modal';
import {logClientError} from '@Libraries/log.library';
import {EVENT_ERROR_LOG_TAG} from '@Components/event-wizard/event-wizard.types';
import {openDesignSelector, openDesignSelectorLegacy} from '@Libraries/design-selector.helper';
import {dataUrlToBlob} from '@Utils/file.util';

/** Replaced on every gallery open, so only the latest open's pick handler is listening. */
let galleryTemplatePickListenerController: AbortController | null = null;

/** Replaced on every editor open, so only the open editor's cover preview is listened for. */
let coverPreviewListenerController: AbortController | null = null;

/** An object URL of the latest cover preview the open editor posted, held until its Save shows it. */
let receivedCoverPreviewUrl: string | null = null;

/** The AI search URIs this page has already warmed: one request per event name and type. */
const warmedEventTemplateSearchUris = new Set<string>();

export const shouldUpsellAITemplateTagging = (doesPAYGUserHaveAITemplateLimit: boolean): boolean => {
  return isPAYGUser() ? !doesPAYGUserHaveAITemplateLimit : false;
};

export const getEventWizardMediaContainerTitle = (mediaType: MediaTypes): string => {
  let title = '';

  if (mediaType === MediaTypes.TEMPLATES) {
    title = window.i18next.t('pmwjs_ai_designs');
  }

  if (mediaType === MediaTypes.MY_DESIGNS) {
    title = window.i18next.t('pmwjs_my_designs');
  }

  if (mediaType === MediaTypes.UPLOAD) {
    title = window.i18next.t('pmwjs_uploads');
  }
  return capitalizeFirstLetterOfAllWords(title);
};

export const openUploadSelector = (): void => {
  openUserMediaModal({
    onAddSelectedCTAText: window.i18next.t('pmwjs_upload_image'),
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.IMAGE,
    mediaTypesToShow: [UploadsMediaType.IMAGE],
    uploadAcceptedMimetype: 'image/*',
    onAddSelected: onSelectedFromUpload,
    hideRecordingOptions: true,
  });
};

export const openDesignSelectorEvents = (onSelected: () => void): void => {
  if (window.PMW.enableMyStuffMultipagesUI) {
    openDesignSelector(
      (pagesData: PosterPageData[]): void => {
        if (pagesData.length === 0) {
          return;
        }
        const firstKey = pagesData[0].posterId;
        const previewUrl = pagesData[0].previewUrl;
        onSelectedFromMyDesignSelector(firstKey, previewUrl);
        onSelected();
      },
      () => {
        window.PMW.setLoading(null);
      },
      false,
      false,
      false
    );
    return;
  }

  openDesignSelectorLegacy(
    (id: string, previewUrl: string | undefined): void => {
      onSelectedFromMyDesignSelector(id, previewUrl);
      onSelected();
    },
    () => {
      window.PMW.setLoading(null);
    }
  );
};

export const openGalleryTemplateSelector = (onSelected: () => void, aiSearchParams?: EventTypeAiSearchParams, opts?: EventsPrefillMorphData, shouldUpsell = false): void => {
  const designGalleryPageUri = getGalleryUriFromEventAiSearchParams(aiSearchParams);

  preloadEmbeddedEditorPlugin();
  listenForGalleryTemplatePick((templateHashedId: string): void => {
    openEmbeddedEditor({hid: templateHashedId, uploadedMediaType: MediaTypes.TEMPLATES, opts, shouldUpsellAIPrefill: shouldUpsell});
    onSelected();
    window.PMW.closeGalleryDialog(window.location.href, false);
  });
  window.PMW.openGalleryDialog('', true, designGalleryPageUri, {showEventsWizardBanner: true, shouldUpsellForSemanticTagging: shouldUpsell}, false);
};

/**
 * Starts the slow parts of a template pick while the user is still deciding: the gallery's AI search for this event
 * and the editor plugin the pick opens. Safe to call repeatedly.
 */
export const warmUpGalleryTemplateSelector = (aiSearchParams: EventTypeAiSearchParams | undefined): void => {
  if (!isUserLoggedIn()) {
    return;
  }
  preloadEmbeddedEditorPlugin();
  warmUpEventTemplateSearch(aiSearchParams);
};

export const onRemoveMedia = (): void => {
  updateMedia(undefined, undefined);
  window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
  togglePreviewCoverPhotoLoading(false);
};

// codeReviewShahrukhLater: refactor into a consolidated thing
export const openEmbeddedEditor = ({hid, uploadedMediaType, opts, shouldUpsellAIPrefill = true, onOpened = noop}: EventWizardEmbeddedEditorOptions): void => {
  const getParams = uploadedMediaType === MediaTypes.TEMPLATES && !shouldUpsellAIPrefill ? {...opts, morphType: MorphType.EVENTS_PREFILL} : {...opts};
  const mode = getEmbeddedEditorMode(uploadedMediaType, !shouldUpsellAIPrefill, opts);
  listenForEditorCoverPreview();
  openDesignInEmbeddedEditor(
    hid,
    onOpened,
    (newHID: string) => {
      onEmbeddedEditorClosed(newHID, uploadedMediaType);
    },
    stopListeningForEditorCoverPreview,
    mode,
    mode === EmbeddedEditorAction.MORPH ? window.i18next.t('pmwjs_done') : undefined,
    {getParams}
  );
};

export const isEmbeddedMorphModeActive = (): boolean => {
  return window.PMW.isEmbedded() && window.PMW.redux.store.getState().posterEditor.posterObject?.mode.details.type === PosterModeType.MORPH;
};

const updateMedia = (
  url: string | undefined,
  uploadedMediaType: MediaTypes | undefined,
  selectedTemplateHID?: string,
  uploadMediaHID?: string
): void => {
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({
      uploadedUrl: url,
      uploadMediaHID: uploadMediaHID,
      mediaType: uploadedMediaType,
      selectedTemplateHID,
      eventPresetImageHash: null,
    })
  );
  void updatePreviewCoverPhoto(url);
  clearCoverPhotoInstantPreview();
};

/**
 * Listens for the editor's own picture of the design while the editor about to open is open. Each open replaces the
 * last one's listener and drops the picture it held, so a Save only ever shows the picture its own editor posted.
 */
const listenForEditorCoverPreview = (): void => {
  stopListeningForEditorCoverPreview();
  const listenerController = new AbortController();
  coverPreviewListenerController = listenerController;
  window.addEventListener('message', onEditorCoverPreviewMessage, {signal: listenerController.signal});
};

const stopListeningForEditorCoverPreview = (): void => {
  coverPreviewListenerController?.abort();
  coverPreviewListenerController = null;
  replaceReceivedCoverPreviewUrl(null);
};

const onEditorCoverPreviewMessage = (event: MessageEvent): void => {
  const coverPreviewDataUrl = getEditorCoverPreviewDataUrl(event);
  if (coverPreviewDataUrl === null) {
    return;
  }
  const coverPreviewUrl = createCoverPreviewObjectUrl(coverPreviewDataUrl);
  if (coverPreviewUrl === null) {
    return;
  }
  replaceReceivedCoverPreviewUrl(coverPreviewUrl);
};

/** The image data URL a cover preview message from the open editor carries, or null for any other message. */
const getEditorCoverPreviewDataUrl = (event: MessageEvent): string | null => {
  if (event.origin !== window.location.origin || !isFromEmbeddedEditor(event)) {
    return null;
  }
  const message: unknown = event.data;
  if (typeof message !== 'string' || !message.startsWith(COVER_PREVIEW_MESSAGE_PREFIX)) {
    return null;
  }
  const dataUrl = message.slice(COVER_PREVIEW_MESSAGE_PREFIX.length);
  return isAcceptedCoverPreviewDataUrl(dataUrl) ? dataUrl : null;
};

const isFromEmbeddedEditor = (event: MessageEvent): boolean => {
  const editorIframe = document.getElementById(EMBEDDED_EDITOR_IFRAME_ID);
  return editorIframe instanceof HTMLIFrameElement && editorIframe.contentWindow !== null && event.source === editorIframe.contentWindow;
};

const isAcceptedCoverPreviewDataUrl = (dataUrl: string): boolean => {
  return (
    dataUrl.length < MAX_COVER_PREVIEW_DATA_URL_LENGTH &&
    COVER_PREVIEW_DATA_URL_PREFIXES.some((prefix: string): boolean => {
      return dataUrl.startsWith(prefix);
    })
  );
};

/** Keeps the image out of Redux as a short object URL. Null when the data URL does not decode. */
const createCoverPreviewObjectUrl = (dataUrl: string): string | null => {
  try {
    return URL.createObjectURL(dataUrlToBlob(dataUrl));
  } catch {
    return null;
  }
};

const replaceReceivedCoverPreviewUrl = (coverPreviewUrl: string | null): void => {
  revokeObjectUrl(receivedCoverPreviewUrl);
  receivedCoverPreviewUrl = coverPreviewUrl;
};

/**
 * Shows the picture the editor posted just before this Save, until the saved design's thumbnail replaces it. A Save
 * without one clears the last pick's, so the cover shows the loader as before.
 */
const showReceivedCoverPreview = (): void => {
  const coverPreviewUrl = receivedCoverPreviewUrl;
  // Handed to the slice, so stopping the listener must not revoke it.
  receivedCoverPreviewUrl = null;
  stopListeningForEditorCoverPreview();
  setCoverPhotoInstantPreviewUrl(coverPreviewUrl);
  if (coverPreviewUrl !== null) {
    void updatePreviewCoverPhoto(coverPreviewUrl);
  }
};

const clearCoverPhotoInstantPreview = (): void => {
  setCoverPhotoInstantPreviewUrl(null);
};

/** Revokes the instant preview it replaces, once the cover no longer shows it. */
const setCoverPhotoInstantPreviewUrl = (coverPreviewUrl: string | null): void => {
  const replacedCoverPreviewUrl = getCoverPhotoInstantPreviewUrl();
  window.PMW.redux.store.dispatch(onEventWizardCoverPhotoInstantPreviewUrlUpdate(coverPreviewUrl));
  revokeObjectUrl(replacedCoverPreviewUrl);
};

const getCoverPhotoInstantPreviewUrl = (): string | null => {
  return window.PMW.redux.store.getState().eventWizard.coverPhotoInstantPreviewUrl;
};

const revokeObjectUrl = (objectUrl: string | null): void => {
  if (objectUrl !== null) {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Listens for the template picked in the gallery about to open, until the pick. Each open replaces the last one's
 * listener, so a gallery closed without a pick never answers a later pick with its stale options.
 */
const listenForGalleryTemplatePick = (onTemplatePicked: (templateHashedId: string) => void): void => {
  galleryTemplatePickListenerController?.abort();
  const listenerController = new AbortController();
  galleryTemplatePickListenerController = listenerController;
  window.addEventListener(
    GALLERY_DESIGN_CLICKED_EVENT,
    (event: Event): void => {
      const templateHashedId = getClickedGalleryTemplateHashedId(event);
      if (templateHashedId === null) {
        return;
      }
      listenerController.abort();
      onTemplatePicked(templateHashedId);
    },
    {signal: listenerController.signal}
  );
};

const getClickedGalleryTemplateHashedId = (event: Event): string | null => {
  if (!(event instanceof CustomEvent)) {
    return null;
  }
  const detail: unknown = event.detail;
  if (typeof detail !== 'object' || detail === null || !('id' in detail) || typeof detail.id !== 'string') {
    return null;
  }
  return detail.id;
};

/**
 * Requests the gallery's AI search URI in the background, so the server has worked out and cached the search term for
 * this event before the gallery asks for the same URI. The redirect is not followed. A failed request is forgotten, so the
 * next warm-up retries it; until then the gallery's own request does the work, as it would without this.
 */
const warmUpEventTemplateSearch = (aiSearchParams: EventTypeAiSearchParams | undefined): void => {
  if (!hasEventTemplateSearchTerm(aiSearchParams)) {
    return;
  }
  const galleryUri = getGalleryUriFromEventAiSearchParams(aiSearchParams);
  if (warmedEventTemplateSearchUris.has(galleryUri)) {
    return;
  }
  warmedEventTemplateSearchUris.add(galleryUri);
  void fetch(window.PMW.util.site_url(galleryUri), {credentials: 'same-origin', redirect: 'manual'}).catch((): void => {
    warmedEventTemplateSearchUris.delete(galleryUri);
  });
};

/** Without an event type the gallery opens the plain events gallery, and without a name there is nothing to search for. */
const hasEventTemplateSearchTerm = (aiSearchParams: EventTypeAiSearchParams | undefined): aiSearchParams is EventTypeAiSearchParams => {
  return aiSearchParams !== undefined && aiSearchParams.eventName.trim() !== '';
};

const onSelectedFromMyDesignSelector = (hid: string, previewUrl?: string): void => {
  updateMedia(previewUrl, MediaTypes.MY_DESIGNS, hid);
};

const getEmbeddedEditorMode = (uploadedMediaType: MediaTypes, isPremium: boolean, opts?: EventsPrefillMorphData): EmbeddedEditorAction => {
  switch (uploadedMediaType) {
    case MediaTypes.MY_DESIGNS:
      return EmbeddedEditorAction.EDIT;
    case MediaTypes.TEMPLATES:
      return isPremium && opts && Object.keys(opts).length > 0 ? EmbeddedEditorAction.MORPH : EmbeddedEditorAction.COPY;
    default:
      throw new Error(`Unhandled case for given media type: ${uploadedMediaType}`);
  }
};

const isPolledHIDStillCurrent = (hid: string): boolean => {
  return window.PMW.redux.store.getState().eventWizard.coverPhoto.selectedTemplateHID === hid;
};

const preloadCoverPhotoImage = (url: string): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => {
      resolve();
    };
    img.onerror = (): void => {
      resolve();
    };
    img.src = url;
  });
};

/**
 * The new thumbnail did not arrive in time (a slow render), but the design itself is saved. Keeps the picture the
 * cover already had, so the user does not lose their cover, and logs the failure instead of showing an error.
 */
const keepCoverPhotoAfterPollError = (coverPhotoUrl: string, err: unknown): void => {
  window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
  restorePreviewCoverPhotoFromInstantPreview(coverPhotoUrl);
  togglePreviewCoverPhotoLoading(false);
  void logClientError(err, {tag: EVENT_ERROR_LOG_TAG});
};

/** Puts the live preview back to the cover's own picture where the instant preview had replaced it. */
const restorePreviewCoverPhotoFromInstantPreview = (coverPhotoUrl: string): void => {
  if (getCoverPhotoInstantPreviewUrl() === null) {
    return;
  }
  void updatePreviewCoverPhoto(coverPhotoUrl);
  clearCoverPhotoInstantPreview();
};

const onEmbeddedEditorClosed = (hid: string, uploadedMediaType: MediaTypes | undefined): void => {
  const currentCoverPhoto = window.PMW.redux.store.getState().eventWizard.coverPhoto;
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({
      ...currentCoverPhoto,
      mediaType: uploadedMediaType,
      selectedTemplateHID: hid,
      eventPresetImageHash: null,
    })
  );
  window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(true));
  togglePreviewCoverPhotoLoading(true);
  showReceivedCoverPreview();

  const handleCoverPhotoPollError = (err: unknown): void => {
    if (!isPolledHIDStillCurrent(hid)) {
      return;
    }
    if (currentCoverPhoto.uploadedUrl) {
      keepCoverPhotoAfterPollError(currentCoverPhoto.uploadedUrl, err);
      return;
    }
    updateMedia(undefined, undefined, undefined);
    window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
    const errMsg = err instanceof Error ? err.message : '';
    openErrorModal({
      message: window.i18next.t('pmwjs_error_message'),
      log: true,
      debugDetails: errMsg + '. ' + EVENT_ERROR_LOG_TAG,
    });
  };

  pollForPosterPreviewGenerationStatus(
    hid,
    async () => {
      try {
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        const previewUrl = await getPosterThumbnailImagePreviewURL(hid);
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        await preloadCoverPhotoImage(previewUrl);
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        updateMedia(previewUrl, uploadedMediaType, hid);
        window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
      } catch (err) {
        handleCoverPhotoPollError(err);
      }
    },
    handleCoverPhotoPollError
  );
};

const onSelectedFromUpload = (images: ImageData[], currentUploadIndex?: number): void => {
  if (currentUploadIndex && currentUploadIndex > 1) {
    return;
  }
  const url = repoImageScreenURL(images[0].hashedFilename, images[0].extension);
  updateMedia(url, MediaTypes.UPLOAD, undefined, images[0].hashedFilename);
};
